/**
 * On-demand data-preview hooks for the schema viewer.
 *
 * Both are LAZY: they only fire when the caller flips ``enabled`` (i.e. the
 * user opens the sample panel for a column / table). This keeps the schema
 * view instant and avoids hammering the external DB — a sample is a live query
 * against the connected database, so it runs only when explicitly requested.
 *
 * ``retry: 0`` — sampling is best-effort; a failure surfaces immediately as an
 * empty/error state rather than stalling the panel with retries.
 */

import { useQuery } from "@tanstack/react-query";

import {
  type SampleColumnResponse,
  type SampleRowsResponse,
  getEnterpriseSampleColumn,
  getEnterpriseSampleRows,
} from "@ando/core/api";

import { enterpriseSampleColumnKey, enterpriseSampleRowsKey } from "./enterpriseKeys";

const SAMPLE_STALE_MS = 60_000;

export function useSampleColumn(
  connId: string,
  objectName: string,
  column: string,
  enabled: boolean,
) {
  return useQuery<SampleColumnResponse>({
    queryKey: enterpriseSampleColumnKey(connId, objectName, column),
    queryFn: () => getEnterpriseSampleColumn(connId, objectName, column),
    enabled: Boolean(enabled && connId && objectName && column),
    staleTime: SAMPLE_STALE_MS,
    retry: 0,
  });
}

export function useSampleRows(connId: string, objectName: string, enabled: boolean) {
  return useQuery<SampleRowsResponse>({
    queryKey: enterpriseSampleRowsKey(connId, objectName),
    queryFn: () => getEnterpriseSampleRows(connId, objectName),
    enabled: Boolean(enabled && connId && objectName),
    staleTime: SAMPLE_STALE_MS,
    retry: 0,
  });
}
